import { LocalStorageKey } from "@/modules/local-storage/constants";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

/** The last size the user dragged the Mem Agent panel to, in CSS pixels. */
export interface MemAgentPanelSize {
  width: number;
  height: number;
}

const isPanelSize = (value: unknown): value is MemAgentPanelSize => {
  if (typeof value !== "object" || value === null) return false;

  const { width, height } = value as Partial<MemAgentPanelSize>;
  return (
    typeof width === "number" &&
    typeof height === "number" &&
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    width > 0 &&
    height > 0
  );
};

export const writeMemAgentPanelSize = (size: MemAgentPanelSize): void => {
  try {
    localStorage.setItem(LocalStorageKey.MemAgentPanelSize, JSON.stringify(size));
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[writeMemAgentPanelSize] Failed.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });
  }
};

export const clearMemAgentPanelSize = (): void => {
  try {
    localStorage.removeItem(LocalStorageKey.MemAgentPanelSize);
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[clearMemAgentPanelSize] Failed.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });
  }
};

export const readMemAgentPanelSize = (): MemAgentPanelSize | null => {
  try {
    const value = localStorage.getItem(LocalStorageKey.MemAgentPanelSize);
    if (value === null) return null;

    const parsed: unknown = (() => {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    })();
    if (isPanelSize(parsed)) return { width: parsed.width, height: parsed.height };

    logger.warn({
      message: "[readMemAgentPanelSize] Invalid panel size found in localStorage, clearing it.",
      info: {
        invalidValue: value,
      },
    });
    clearMemAgentPanelSize();
    return null;
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[readMemAgentPanelSize] Failed.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });

    return null;
  }
};
